import {
  Business as OfficeIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Typography,
  Collapse,
  TextField,
  Checkbox,
  FormControlLabel,
  Avatar
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'

import {
  AppBarR,
  Wrapper,
  RoleDisplay,
  RoleBasedActions
} from '../components/common'
import { ListDesks } from '../components/desk'
import MultiParagraph from '../components/MultiParagraph'
import { OfficeSubscriptionStatus } from '../components/subscription'
import db from '../db/db'
import { AnalyticsService } from '../services/AnalyticsService'
import OfficeService from '../services/OfficeService'
import { canAddUser } from '../services/subscriptionService'
import { sessionState } from '../state'
import { AnalyticsEvents } from '../types/AnalyticsEvents'
import Database from '../types/Database'
import { addRolesToOffices, OfficeWithRole } from '../utils/profileHelpers'
import { hasPermission } from '../utils/rolePermissions'

import routes from './routes'

/**
 * ViewOffice - Office detail view page component
 *
 * Displays office information and associated desks.
 * Allows navigation to edit office and create new desks.
 */
const ViewOffice: React.FC = () => {
  const { officeId } = useParams<{ officeId: string }>()
  const navigate = useNavigate()
  const [session, setSession] = useRecoilState(sessionState)
  const { user, isAuthenticating } = session

  const [office, setOffice] = useState<OfficeWithRole | null>(null)
  const [desks, setDesks] = useState<Database.Desk[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [allowManageOffice, setAllowManageOffice] = useState(false)
  const [inviteSuccess, setInviteSuccess] = useState('')
  const [inviteError, setInviteError] = useState('')
  const [inviteSubmitting, setInviteSubmitting] = useState(false)
  const [canInvite, setCanInvite] = useState(true)
  const [capacityTooltip, setCapacityTooltip] = useState('')
  const [subscriptionRefreshTrigger, setSubscriptionRefreshTrigger] =
    useState(0)

  // Delete office state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')

  // Fetch office details and desks
  useEffect(() => {
    const fetchOfficeData = async () => {
      if (!user || !officeId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Fetch the office directly from Firestore instead of relying on session state
        const officeData = await db.getById('offices', officeId)

        if (!officeData) {
          setError('Office not found or you do not have access to it.')
          setLoading(false)
          return
        }

        // Get office managers to determine user role
        let officeManagers: Database.OfficeManager[] = []
        try {
          officeManagers = await db.query({
            collection: 'officeManagers',
            where: ['officeId', '==', officeId]
          })
        } catch (err) {
          console.warn('Could not fetch office managers:', err)
        }

        // Add role to office
        const officesWithRoles = addRolesToOffices(
          [officeData],
          user,
          officeManagers
        )
        setOffice(officesWithRoles[0])

        // Fetch desks for this office
        const officeDesks = await db.query({
          collection: 'desks',
          where: ['office', '==', officeId]
        })
        setDesks(officeDesks)

        // Check capacity for invite button
        if (officeId) {
          const hasCapacity = await canAddUser(officeId)
          setCanInvite(hasCapacity)

          if (!hasCapacity) {
            // Get capacity info for tooltip
            const { getCapacityInfo } = await import(
              '../services/subscriptionService'
            )
            const capacityInfo = await getCapacityInfo(officeId)
            const { getTierById } = await import('../config/subscriptionTiers')
            const tierName = getTierById(capacityInfo.tier).name
            setCapacityTooltip(
              `This office has reached its user capacity limit (${capacityInfo.capacity} users for ${tierName} tier). Upgrade to add more users.`
            )
          }
        }
      } catch (err) {
        console.error('Error fetching office data:', err)
        setError('Failed to load office information. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchOfficeData()
  }, [user, officeId])

  // Handle invite form submission
  const handleInviteSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()

    if (!office || !user) return

    setInviteSubmitting(true)
    setInviteError('')
    setInviteSuccess('')

    try {
      const emails = inviteEmail
        .split(',')
        .map(email => email.trim().toLowerCase())
        .filter(email => email)

      if (emails.length === 0) {
        setInviteError('Please enter at least one email address.')
        return
      }

      // Check capacity before sending invitations
      if (office.oid) {
        const hasCapacity = await canAddUser(office.oid)
        if (!hasCapacity) {
          setInviteError(
            'This office has reached its user capacity limit. Please upgrade your subscription to add more users.'
          )
          return
        }
      }

      for (const email of emails) {
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
          setInviteError(`Invalid email format: ${email}`)
          return
        }

        // Find user by email and add them to the office
        const existingUsers = await db.query({
          collection: 'users',
          where: ['email', '==', email.toLowerCase()]
        })

        if (existingUsers.length > 0 && office.oid) {
          const invitedUser = existingUsers[0]

          // Add office to user's officeids array if not already there
          const currentOfficeIds = invitedUser.officeids || []
          if (!currentOfficeIds.includes(office.oid)) {
            await db.updateItem('users', invitedUser.id as string, {
              officeids: [...currentOfficeIds, office.oid]
            })
          }

          // If allow manage office is checked, create office manager record
          if (allowManageOffice) {
            const officeManagerData = {
              id: `${office.oid}_${invitedUser.uid}`,
              officeId: office.oid,
              userId: invitedUser.uid,
              email: email.toLowerCase(),
              addedBy: user.uid || undefined,
              addedOnDate: new Date()
            }
            await db.addItem('officeManagers', officeManagerData)
          }
        } else {
          // User doesn't exist yet, create invite record for when they sign up
          const inviteData = {
            oid: office.oid,
            invitedOnDate: new Date(),
            invitedByUid: user.uid ?? '',
            email: email.toLowerCase()
          }

          await db.addItem('invite', inviteData)

          // Track office invitation sent event
          AnalyticsService.trackEvent(AnalyticsEvents.INVITE_SENT, {
            invitation_type: 'office'
          })

          // If allow manage office is checked, create office manager record with email
          if (allowManageOffice && office.oid) {
            const officeManagerData = {
              id: `${office.oid}_${email.toLowerCase()}`,
              officeId: office.oid,
              userId: null, // Will be set when user signs up
              email: email.toLowerCase(),
              addedBy: user.uid || undefined,
              addedOnDate: new Date()
            }
            await db.addItem('officeManagers', officeManagerData)
          }
        }
      }

      setInviteSuccess(
        `Successfully sent ${emails.length} invitation${
          emails.length > 1 ? 's' : ''
        }!`
      )
      setInviteEmail('')
      setAllowManageOffice(false)
      setShowInviteForm(false)

      // Trigger subscription status refresh
      setSubscriptionRefreshTrigger(prev => prev + 1)
    } catch (err) {
      console.error('Error sending invites:', err)
      setInviteError('Failed to send invitations. Please try again.')
    } finally {
      setInviteSubmitting(false)
    }
  }

  // Clear invite messages
  const clearInviteMessages = () => {
    setInviteSuccess('')
    setInviteError('')
  }

  // Handle desk editing
  const handleDeskEdit = (desk: Database.Desk) => {
    if (desk.id) {
      navigate(`/edit-desk/${desk.id}`)
    }
  }

  // Handle desk deletion
  const handleDeskDelete = async (deskId: string) => {
    if (!window.confirm('Are you sure you want to delete this desk?')) {
      return
    }

    try {
      await db.deleteItem('desks', deskId)
      // Refresh desks list
      setDesks(prevDesks => prevDesks.filter(desk => desk.id !== deskId))
    } catch (error) {
      console.error('Error deleting desk:', error)
      // You might want to show an error message to the user here
    }
  }

  // Handle office soft-delete
  const handleDeleteOffice = async () => {
    if (!office?.oid || !user?.uid) return

    setIsDeleting(true)
    setDeleteError('')

    try {
      await OfficeService.softDeleteOffice(office.oid, user.uid)

      // Remove from session state
      const updatedOffices = (session.offices || []).filter(
        o => o.oid !== office.oid
      )
      setSession(old => ({
        ...old,
        offices: updatedOffices
      }))

      setDeleteDialogOpen(false)
      // Navigate to offices list after deletion
      navigate(routes.offices)
    } catch (err) {
      console.error('Error deleting office:', err)
      setDeleteError(
        err instanceof Error ? err.message : 'Failed to delete office. Please try again.'
      )
    } finally {
      setIsDeleting(false)
    }
  }

  // Redirect unauthenticated users to sign-in page
  if (!isAuthenticating && !user) {
    return <Navigate to={routes.signin} replace />
  }

  // Show loading state during authentication
  if (isAuthenticating) {
    return (
      <>
        <AppBarR />
        <Wrapper>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            minHeight='200px'
          >
            <CircularProgress />
            <Typography variant='body1' sx={{ ml: 2 }}>
              Loading office...
            </Typography>
          </Box>
        </Wrapper>
      </>
    )
  }

  // Handle error states
  if (error) {
    return (
      <>
        <AppBarR />
        <Wrapper>
          <Box sx={{ p: 2 }}>
            <Alert severity='error' sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button
              variant='contained'
              onClick={() => navigate(routes.profile)}
              sx={{ mt: 2 }}
            >
              Back to Profile
            </Button>
          </Box>
        </Wrapper>
      </>
    )
  }

  // Show loading state
  if (loading) {
    return (
      <>
        <AppBarR />
        <Wrapper>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            minHeight='200px'
          >
            <CircularProgress />
            <Typography variant='body1' sx={{ ml: 2 }}>
              Loading office details...
            </Typography>
          </Box>
        </Wrapper>
      </>
    )
  }

  // Handle case where office is not found
  if (!office) {
    return (
      <>
        <AppBarR />
        <Wrapper>
          <Box sx={{ p: 2 }}>
            <Alert severity='warning' sx={{ mb: 2 }}>
              Office not found or you do not have access to it.
            </Alert>
            <Button
              variant='contained'
              onClick={() => navigate(routes.profile)}
              sx={{ mt: 2 }}
            >
              Back to Profile
            </Button>
          </Box>
        </Wrapper>
      </>
    )
  }

  const canManageOffice = office
    ? hasPermission(office, 'canEditOffice')
    : false
  const canInviteUsers = office
    ? hasPermission(office, 'canInviteUsers')
    : false
  const canCreateDesks = office
    ? hasPermission(office, 'canCreateDesks')
    : false
  const canDeleteDesks = office
    ? hasPermission(office, 'canDeleteDesks')
    : false

  return (
    <>
      <AppBarR />
      <Wrapper>
        <Box sx={{ p: 2 }}>
          {/* Office Header */}
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            mb={3}
            flexWrap='wrap'
            gap={2}
          >
            <Box
              display='flex'
              alignItems='center'
              gap={2}
              flex={1}
              minWidth={0}
            >
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <OfficeIcon fontSize='small' />
              </Avatar>
              <Typography
                component='h1'
                variant='h6'
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {office.name || 'Unnamed Office'}
              </Typography>
            </Box>

            {/* Role-based action buttons */}
            <Box flexShrink={0} display='flex' gap={1} alignItems='center'>
              <RoleBasedActions
                office={office}
                onEdit={() => navigate(`/edit-office/${office.oid}`)}
                onInvite={() => setShowInviteForm(!showInviteForm)}
                size='medium'
                variant='spaced'
                inviteDisabled={!canInvite}
                inviteDisabledTooltip={capacityTooltip}
              />
              {canManageOffice && (
                <Button
                  variant='outlined'
                  color='error'
                  size='medium'
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete
                </Button>
              )}
            </Box>
          </Box>

          <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
            View office details, manage desks, and invite team members
          </Typography>

          {/* User Role Display */}
          <Box mb={3}>
            <RoleDisplay
              role={office.userRole}
              variant='expanded'
              size='medium'
              showDescription={true}
            />
          </Box>

          {/* Subscription Status */}
          {office.oid && (
            <Box mb={3}>
              <OfficeSubscriptionStatus
                officeId={office.oid}
                refreshTrigger={subscriptionRefreshTrigger}
              />
            </Box>
          )}

          {/* Invite Messages */}
          {(inviteSuccess || inviteError) && (
            <Box mb={2}>
              {inviteSuccess && (
                <Alert
                  severity='success'
                  onClose={clearInviteMessages}
                  sx={{ mb: 1 }}
                >
                  {inviteSuccess}
                </Alert>
              )}
              {inviteError && (
                <Alert
                  severity='error'
                  onClose={clearInviteMessages}
                  sx={{ mb: 1 }}
                >
                  {inviteError}
                </Alert>
              )}
            </Box>
          )}

          {/* Invite Form */}
          <Collapse in={showInviteForm && canInviteUsers}>
            <Card variant='outlined' sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3
                  }}
                >
                  <Avatar
                    sx={{
                      mr: 2,
                      bgcolor: 'primary.main',
                      width: 48,
                      height: 48
                    }}
                  >
                    <PersonAddIcon />
                  </Avatar>
                  <Typography variant='h6' component='h2'>
                    Invite Users to {office.name}
                  </Typography>
                </Box>

                <Box component='form' onSubmit={handleInviteSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        id='inviteEmail'
                        label='Email Addresses'
                        name='inviteEmail'
                        value={inviteEmail}
                        onChange={e => setInviteEmail(e.target.value)}
                        disabled={inviteSubmitting}
                        helperText='Enter one or more email addresses separated by commas'
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          backgroundColor: 'rgba(102, 126, 234, 0.05)'
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={allowManageOffice}
                              onChange={e =>
                                setAllowManageOffice(e.target.checked)
                              }
                              disabled={inviteSubmitting}
                              color='primary'
                            />
                          }
                          label={
                            <Typography
                              variant='body1'
                              sx={{ fontWeight: 500 }}
                            >
                              Allow to manage the office
                            </Typography>
                          }
                        />
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ mt: 1, ml: 4 }}
                        >
                          This person will be able to create and manage desks in
                          this office
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box display='flex' gap={2} mt={3}>
                    <Button
                      type='submit'
                      variant='contained'
                      disabled={inviteSubmitting || !inviteEmail.trim()}
                      sx={{
                        borderRadius: 2,
                        minWidth: 120
                      }}
                    >
                      {inviteSubmitting ? 'Sending...' : 'Send Invite'}
                    </Button>
                    <Button
                      variant='outlined'
                      onClick={() => {
                        setShowInviteForm(false)
                        setInviteEmail('')
                        setAllowManageOffice(false)
                        clearInviteMessages()
                      }}
                      disabled={inviteSubmitting}
                      sx={{ borderRadius: 2 }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Collapse>

          <Grid container spacing={3}>
            {/* Office Information Card */}
            <Grid item xs={12}>
              <Card variant='outlined' sx={{ height: 'fit-content' }}>
                <CardContent>
                  <Typography variant='h6' component='h2' gutterBottom>
                    Office Information
                  </Typography>

                  {office.description && (
                    <Box mb={2}>
                      <Typography
                        variant='subtitle2'
                        color='text.secondary'
                        gutterBottom
                      >
                        Description
                      </Typography>
                      <MultiParagraph text={office.description} />
                    </Box>
                  )}

                  {office.address && (
                    <Box mb={2}>
                      <Typography
                        variant='subtitle2'
                        color='text.secondary'
                        gutterBottom
                      >
                        Address
                      </Typography>
                      <Typography variant='body2'>{office.address}</Typography>
                    </Box>
                  )}

                  {office.contactInfo && (
                    <Box mb={2}>
                      <Typography
                        variant='subtitle2'
                        color='text.secondary'
                        gutterBottom
                      >
                        Contact Information
                      </Typography>
                      <Typography variant='body2'>
                        {office.contactInfo}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      gutterBottom
                    >
                      Your Role & Permissions
                    </Typography>
                    <RoleDisplay
                      role={office.userRole}
                      variant='expanded'
                      size='small'
                      showDescription={true}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Desks Section */}
            <Grid item xs={12}>
              <Card variant='outlined'>
                <CardContent>
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='space-between'
                    mb={2}
                  >
                    <Typography variant='h6' component='h2'>
                      Desks ({desks.length})
                    </Typography>

                    {canManageOffice && (
                      <Button
                        variant='contained'
                        size='small'
                        startIcon={<AddIcon />}
                        onClick={() => navigate(`/create-desk/${office.oid}`)}
                      >
                        Add Desk
                      </Button>
                    )}
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {desks.length > 0 ? (
                    <ListDesks
                      desks={desks}
                      uid={user?.uid}
                      selectedDesks={[]}
                      onDeskClicked={() => {}} // No-op for read-only view
                      onDeskDeleted={canDeleteDesks ? handleDeskDelete : null}
                      onDeskEdit={canCreateDesks ? handleDeskEdit : null}
                      readOnly={false}
                      title=''
                    />
                  ) : (
                    <Box
                      display='flex'
                      flexDirection='column'
                      alignItems='center'
                      py={4}
                    >
                      <Typography variant='body2' color='text.secondary' mb={2}>
                        No desks have been created for this office yet.
                      </Typography>
                      {canCreateDesks && (
                        <Button
                          variant='outlined'
                          startIcon={<AddIcon />}
                          onClick={() => navigate(`/create-desk/${office.oid}`)}
                        >
                          Create First Desk
                        </Button>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Back Button */}
          <Box mt={3}>
            <Button variant='text' onClick={() => navigate(routes.profile)}>
              ← Back to Profile
            </Button>
          </Box>
        </Box>

        {/* Delete Office Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          aria-labelledby='delete-office-dialog-title'
        >
          <DialogTitle id='delete-office-dialog-title'>
            Delete Office
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{office?.name}"? The office will
              be hidden from all users and will no longer be available for
              bookings. An administrator can restore it later if needed.
            </DialogContentText>
            {deleteError && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {deleteError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteOffice}
              color='error'
              variant='contained'
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Office'}
            </Button>
          </DialogActions>
        </Dialog>
      </Wrapper>
    </>
  )
}

export default ViewOffice
